import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { HID_EMAIL_MESSAGE, isHidGlobalEmail } from "../auth/hidEmail";
import {
  createRecoveryFlow,
  RecoveryFlow,
  requestRecoveryCode,
  submitRecoveryCode,
} from "../auth/kratosRecovery";
import { AlreadySignedIn } from "../auth/kratosRegistration";
import { setPasswordOnSettingsFlow } from "../auth/kratosSettings";

type Step = "email" | "code" | "password" | "done";

const FLOW_UNAVAILABLE =
  "Cannot reach the identity service. Confirm the Ory stack is running on port 4433.";

interface RecoveryFormProps {
  onBackToLogin: () => void;
}

export function RecoveryForm({ onBackToLogin }: RecoveryFormProps) {
  const { refresh } = useAuth();

  const [flow, setFlow] = useState<RecoveryFlow | null>(null);
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [settingsFlowId, setSettingsFlowId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const startFlow = useCallback(async () => {
    try {
      setFlow(await createRecoveryFlow());
    } catch (err) {
      if (err instanceof AlreadySignedIn) {
        await refresh();
        return;
      }
      setError(FLOW_UNAVAILABLE);
    }
  }, [refresh]);

  useEffect(() => {
    void startFlow();
  }, [startFlow]);

  const trimmedEmail = email.trim();

  const handleRequestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!trimmedEmail) {
      setError("Enter the email address of your account.");
      return;
    }
    if (!isHidGlobalEmail(trimmedEmail)) {
      setError(HID_EMAIL_MESSAGE);
      return;
    }
    if (!flow) {
      setError(FLOW_UNAVAILABLE);
      return;
    }
    setBusy(true);
    try {
      const result = await requestRecoveryCode(flow, trimmedEmail);
      if (result.kind === "codeSent") {
        setFlow(result.flow);
        setInfo(result.message);
        setStep("code");
      } else if (result.kind === "expired") {
        setError("That attempt timed out. Please try again.");
        await startFlow();
      } else if (result.kind === "error") {
        if (result.flow) setFlow(result.flow);
        setError(result.message);
      }
    } catch {
      setError(FLOW_UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!code.trim()) {
      setError("Enter the code from your email.");
      return;
    }
    if (!flow) {
      setError(FLOW_UNAVAILABLE);
      return;
    }
    setBusy(true);
    try {
      const result = await submitRecoveryCode(flow, code.trim());
      if (result.kind === "recovered") {
        setSettingsFlowId(result.settingsFlowId);
        setInfo("Code accepted. Choose a new password.");
        setStep("password");
      } else if (result.kind === "expired") {
        setError("That attempt timed out. Please start again.");
        setStep("email");
        setCode("");
        await startFlow();
      } else {
        if (result.flow) setFlow(result.flow);
        setError(result.message);
      }
    } catch {
      setError(FLOW_UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!flow) return;
    setBusy(true);
    setError(null);
    try {
      const result = await requestRecoveryCode(flow, trimmedEmail);
      if (result.kind === "codeSent") {
        setFlow(result.flow);
        setInfo("We sent a new code.");
      } else if (result.kind === "error") {
        setError(result.message);
      }
    } catch {
      setError(FLOW_UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  };

  const handleSetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const result = await setPasswordOnSettingsFlow(settingsFlowId, password);
      if (result.kind === "ok") {
        setStep("done");
        await refresh();
      } else {
        setError(result.message);
      }
    } catch {
      setError(FLOW_UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  };

  const fieldClass =
    "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20";
  const primaryButton =
    "rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50";

  const banners = (
    <>
      {error && (
        <div role="alert" className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}
      {info && !error && (
        <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
          {info}
        </div>
      )}
    </>
  );

  if (step === "done") {
    return (
      <div className="mt-6 rounded-xl border border-green-200 bg-green-50 px-4 py-6 text-center">
        <div className="text-lg font-semibold text-green-900">Password updated</div>
        <p className="mt-2 text-sm text-green-800">You are signed in. Taking you in…</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h2 className="mb-4 text-base font-semibold text-slate-800">Reset your password</h2>
      {banners}

      {step === "email" && (
        <form onSubmit={handleRequestCode} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email address</span>
            <input
              type="email"
              value={email}
              autoComplete="username"
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@hidglobal.com"
              className={fieldClass}
              disabled={busy}
            />
          </label>
          <button type="submit" disabled={busy || !flow} className={primaryButton}>
            {busy ? "Sending code…" : "Email me a reset code"}
          </button>
          <p className="text-xs text-slate-500">
            We email a 6-digit code. It is valid for 15 minutes.
          </p>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={handleSubmitCode} className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            Enter the code sent to <span className="font-semibold">{trimmedEmail}</span>.
          </p>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reset code</span>
            <input
              type="text"
              value={code}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className={`${fieldClass} tracking-[0.4em]`}
              disabled={busy}
            />
          </label>
          <button type="submit" disabled={busy} className={primaryButton}>
            {busy ? "Verifying…" : "Continue"}
          </button>
          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => {
                setStep("email");
                setCode("");
                setInfo(null);
                setError(null);
              }}
              disabled={busy}
              className="font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Use a different email
            </button>
            <button
              type="button"
              onClick={() => void handleResend()}
              disabled={busy}
              className="font-semibold text-brand-navy hover:underline disabled:opacity-50"
            >
              Resend code
            </button>
          </div>
        </form>
      )}

      {step === "password" && (
        <form onSubmit={handleSetPassword} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">New password</span>
            <input
              type="password"
              value={password}
              autoComplete="new-password"
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className={fieldClass}
              disabled={busy}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Confirm new password</span>
            <input
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              className={fieldClass}
              disabled={busy}
            />
          </label>
          <button type="submit" disabled={busy} className={primaryButton}>
            {busy ? "Saving…" : "Set new password"}
          </button>
        </form>
      )}

      <div className="mt-6 border-t border-slate-200 pt-5">
        <p className="text-sm text-slate-600">
          Remembered it?{" "}
          <button type="button" onClick={onBackToLogin} className="font-semibold text-brand-navy hover:underline">
            Back to sign in
          </button>
        </p>
      </div>
    </div>
  );
}
